import { useMutation, useQuery, useSubscription } from '@apollo/client';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  AlertDescription,
  Button,
  Content,
  Icon,
  LoadingIcon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  CarePlanCallout,
  CarePlanReadOnlyView,
  CarePlanTab,
  useAppointmentCarePlanDraft,
} from '@eluve/feature-care-plans';
import {
  CARE_PLAN_TAB,
  SUMMARY_TAB,
  TRANSCRIPT_TAB,
  isDocumentTab,
  listenAppointmentStatusSubscription,
  toDocumentTabId,
  useAppointmentActivity,
  useAppointmentHeaderDetails,
  useAppointmentMode,
  useAppointmentStatus,
  useAppointmentTabNav,
  useAppointmentTranscriptionSync,
  useIsAppointmentReadonly,
  useListenForAppointmentEnd,
  useListenForSummary,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import {
  AppointmentTabBar,
  AppointmentTabContent,
  CreateDocumentButton,
  type DocGenState,
  DocumentGeneratingTabContent,
  DocumentTabContent,
  PdfDocumentTabContent,
  type PdfFillState,
  PdfFillTabContent,
  deleteDocumentReferenceMutation,
  getDocumentReferencesByAppointmentQuery,
  updateDocumentReferenceTitleMutation,
} from '@eluve/frontend-feature-appointment';
import { DbEnums } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { ALL_MINI_APPS, MiniAppSummarySideRail } from '@eluve/mini-apps';
import { carePlanShapeOf } from '@eluve/plan-shapes';
import { useAnalytics } from '@eluve/posthog-react';
import {
  AppointmentName,
  FeatureFlaggedComponent,
  MicrophoneRequestAlert,
  useIsFeatureFlagEnabled,
} from '@eluve/smart-blocks';

import { appConfig } from '../../../../../config';

import { AdditionalNotes } from './AdditionalNotes';
import {
  EluveAdminControlsPanel,
  EluveAdminControlsProvider,
  EluveAdminControlsToggle,
} from './EluveAdminControls';
import { NewAppointmentForm } from './NewAppointmentForm';
import { appointmentLayoutStyles } from './appointmentLayoutStyles';
import { AppointmentDetails } from './components/AppointmentDetails';
import { AppointmentSummaryFooter } from './components/AppointmentSummaryFooter';
import { AppointmentSummaryResult } from './components/AppointmentSummaryResult';
import { AppointmentTranscription } from './components/AppointmentTranscription';
import { AppointmentTranscriptionFooter } from './components/AppointmentTranscriptionFooter';
import { HiddenForManualCharting } from './components/HiddenForManualCharting';
import { AppointmentInsights } from './components/Insights/AppointmentInsights';

export interface ActiveAppointmentLayoutProps {
  tenantId: string;
  appointmentId: string;
}
export const ActiveAppointmentLayout: React.FC<
  ActiveAppointmentLayoutProps
> = ({ appointmentId, tenantId }) => {
  const { capture } = useAnalytics();
  const isInsightsEnabled = useIsFeatureFlagEnabled('INSIGHTS');
  const isDocGenEnabled = useFeatureFlagEnabled('DOCUMENT_GENERATION') ?? false;
  const skipNotStartedAppointmentLayout = useIsFeatureFlagEnabled(
    'SKIP_NOT_STARTED_APPOINTMENT_LAYOUT',
  );
  const toast = useToast();
  const logger = useNamedLogger('ActiveAppointmentLayout');
  const apiClient = useApiClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfFill, setPdfFill] = useState<PdfFillState | null>(null);
  const pdfFillRequestRef = useRef(0);
  const [docGen, setDocGen] = useState<DocGenState | null>(null);
  const docGenRequestRef = useRef(0);
  const lastGenParamsRef = useRef<{
    prompt?: string;
    templateId?: string;
    content?: string;
    instructions?: string;
    docType?: string;
  } | null>(null);

  const PDF_FILL_DOC_ID = 'pdf-form-fill';
  const DOC_GEN_ID = 'doc-gen';

  const { tab, setTab } = useAppointmentTabNav();
  const appointmentStatus = useAppointmentStatus();

  // End-of-encounter draft Care Plan (ENG-4699/ENG-4706): the tab + callout
  // track the generation job's state — preparing → ready → none/failed. Inert
  // (`carePlanState === 'idle'`) unless Medplum is on and the appointment has
  // completed — the lifecycle moment the backend enqueues the job. The job
  // `Task`, not the appointment status, drives the state once we start polling.
  const {
    carePlan,
    state: carePlanState,
    refetch: refetchCarePlan,
  } = useAppointmentCarePlanDraft(
    appointmentId,
    appointmentStatus === 'COMPLETED',
  );
  const [carePlanCalloutDismissed, setCarePlanCalloutDismissed] =
    useState(false);
  const headerDetails = useAppointmentHeaderDetails();
  const patientId = headerDetails?.patientId;
  const isReadonly = useIsAppointmentReadonly();

  useListenForSummary({
    onCompleted: () => {
      toast.success('Summary is ready!', {
        description: 'Your summary is ready to be reviewed.',
      });
    },
  });
  useListenForAppointmentEnd();

  // Listen for meaningful status changes for this appointment
  useSubscription(listenAppointmentStatusSubscription, {
    variables: {
      tenantId,
      appointmentId,
    },
  });

  // Sync chunked transcript data into the apollo cache for use across the component tree
  useAppointmentTranscriptionSync();

  const { isSummaryAvailable } = useSummary();
  const appointmentMode = useAppointmentMode();
  const { isSummarizationInProgress, isTranscriptionInProgress } =
    useAppointmentActivity();

  const summaryTabEnabled =
    (appointmentMode === 'AUDIO_COPILOT' &&
      (appointmentStatus !== 'ACTIVE' || isSummaryAvailable)) ||
    appointmentMode === 'MANUAL_CHARTING';

  // Document references for this appointment
  const { data: docsData, refetch: refetchDocs } = useQuery(
    getDocumentReferencesByAppointmentQuery,
    {
      variables: { tenantId, appointmentId },
      skip: !isDocGenEnabled,
    },
  );
  const documents = docsData?.documentReferences ?? [];

  const [deleteDocument] = useMutation(deleteDocumentReferenceMutation);
  const [renameDocument] = useMutation(updateDocumentReferenceTitleMutation);

  const handlePdfFillStop = () => {
    // Bump ref so any in-flight request is discarded
    pdfFillRequestRef.current += 1;
    setPdfFill(null);
    if (tab === toDocumentTabId(PDF_FILL_DOC_ID)) {
      setTab(SUMMARY_TAB);
    }
  };

  const handleDocGenStop = () => {
    docGenRequestRef.current += 1;
    setDocGen(null);
    setIsGenerating(false);
    if (tab === toDocumentTabId(DOC_GEN_ID)) {
      setTab(SUMMARY_TAB);
    }
  };

  const handleDocumentDelete = async (documentId: string) => {
    if (documentId === PDF_FILL_DOC_ID) {
      handlePdfFillStop();
      return;
    }
    if (documentId === DOC_GEN_ID) {
      handleDocGenStop();
      return;
    }
    try {
      await deleteDocument({ variables: { tenantId, id: documentId } });
      capture('document_deleted', { document_id: documentId });
      if (tab === toDocumentTabId(documentId)) setTab(SUMMARY_TAB);
      refetchDocs();
      toast.success('Document deleted');
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleDocumentRename = async (documentId: string, newTitle: string) => {
    if (documentId === PDF_FILL_DOC_ID || documentId === DOC_GEN_ID) return;
    try {
      await renameDocument({
        variables: { tenantId, id: documentId, title: newTitle },
      });
      refetchDocs();
    } catch {
      toast.error('Failed to rename document');
    }
  };

  const handleGenerate = async (params: {
    prompt?: string;
    templateId?: string;
    content?: string;
    instructions?: string;
    docType?: string;
  }) => {
    if (!patientId) return;

    // Mirror the PDF-fill flow: open a "Preparing your document" tab immediately
    // and swap to the real document when generation resolves.
    lastGenParamsRef.current = params;
    docGenRequestRef.current += 1;
    const requestId = docGenRequestRef.current;
    setDocGen({ status: 'preparing', title: 'New document' });
    setTab(toDocumentTabId(DOC_GEN_ID));
    setIsGenerating(true);
    try {
      const result = await apiClient.documents.generateDocument({
        params: { tenantId },
        body: {
          patientId,
          appointmentId,
          ...(params.prompt ? { prompt: params.prompt } : {}),
          ...(params.templateId ? { templateId: params.templateId } : {}),
        },
      });

      // Discard if the user stopped or started a newer generation.
      if (docGenRequestRef.current !== requestId) return;

      if (result.status === 200) {
        capture('document_generated', {
          document_id: result.body.id,
          origin: params.templateId ? 'template' : 'prompt',
          tenant_id: tenantId,
          appointment_id: appointmentId,
          patient_id: patientId,
          doc_type: params.docType,
          template_id: params.templateId,
          prompt_length: params.prompt?.length,
        });

        await refetchDocs();
        if (docGenRequestRef.current !== requestId) return;
        setDocGen(null);
        setTab(toDocumentTabId(result.body.id));
        toast.success('Document generated');
      } else {
        setDocGen({ status: 'error', title: 'New document' });
      }
    } catch {
      if (docGenRequestRef.current !== requestId) return;
      setDocGen({ status: 'error', title: 'New document' });
    } finally {
      if (docGenRequestRef.current === requestId) setIsGenerating(false);
    }
  };

  const handleFillPdfForm = async (
    templateId: string,
    templateName: string,
  ) => {
    if (!patientId) return;

    // Stale-guard: bump the request counter so an in-flight response from a
    // previous fill is ignored once a new fill starts.
    pdfFillRequestRef.current += 1;
    const requestId = pdfFillRequestRef.current;

    setPdfFill({ status: 'preparing', templateId, title: templateName });
    setTab(toDocumentTabId(PDF_FILL_DOC_ID));

    try {
      const res = await apiClient.documents.fillPdfFormFromTemplate({
        params: { tenantId },
        body: { templateId, patientId, appointmentId },
      });

      // Discard if a newer fill has started
      if (pdfFillRequestRef.current !== requestId) return;

      if (res.status === 200) {
        capture('pdf_form_filled', { templateId });
        // The filled PDF is persisted as a document_references row; surface it
        // as a normal document tab so it survives reloads.
        await refetchDocs();
        if (pdfFillRequestRef.current !== requestId) return;
        setPdfFill(null);
        setTab(toDocumentTabId(res.body.documentId));
      } else {
        setPdfFill({
          status: 'error',
          templateId,
          title: templateName,
        });
      }
    } catch (error) {
      if (pdfFillRequestRef.current !== requestId) return;
      logger.error('PDF form fill failed', { error });
      setPdfFill({
        status: 'error',
        templateId,
        title: templateName,
        message:
          error instanceof Error ? error.message : 'An unknown error occurred.',
      });
    }
  };

  return (
    <>
      <AppointmentDetails appointmentId={appointmentId} tenantId={tenantId} />
      <Content>
        {/* Grid shape is DetailsSidebar's fixed-variant contract: its aside
            self-places into col 2, rows 2-3. */}
        <div className="grid h-full min-h-0 grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_minmax(0,1fr)_auto]">
          <div className="col-span-2 border-b border-border-primary pt-2.5 pb-[3px]">
            <div className="mx-auto max-w-[1024px] px-1">
              <AppointmentTabBar
                summaryDisabled={!summaryTabEnabled}
                transcriptLoading={isTranscriptionInProgress}
                summaryLoading={isSummarizationInProgress}
                documents={[
                  ...(isDocGenEnabled ? documents : []),
                  ...(pdfFill
                    ? [
                        {
                          id: PDF_FILL_DOC_ID,
                          title: pdfFill.title,
                          loading: pdfFill.status === 'preparing',
                        },
                      ]
                    : []),
                  ...(docGen
                    ? [
                        {
                          id: DOC_GEN_ID,
                          title: docGen.title,
                          loading: docGen.status === 'preparing',
                        },
                      ]
                    : []),
                ]}
                carePlanAvailable={
                  carePlanState === 'generating' || carePlanState === 'ready'
                }
                carePlanGenerating={carePlanState === 'generating'}
                onDocumentDelete={
                  isDocGenEnabled || pdfFill || docGen
                    ? handleDocumentDelete
                    : undefined
                }
                onDocumentRename={
                  isDocGenEnabled ? handleDocumentRename : undefined
                }
                trailing={
                  isDocGenEnabled ? (
                    <CreateDocumentButton
                      onGenerate={handleGenerate}
                      isGenerating={
                        isGenerating || pdfFill?.status === 'preparing'
                      }
                      onFillPdfForm={handleFillPdfForm}
                      disabled={!patientId}
                      disabledReason="Select a patient first to create documents"
                    />
                  ) : undefined
                }
              />
            </div>
          </div>

          <div className="col-start-1 row-start-2 min-h-0 overflow-y-auto p-3">
            <Stack className={appointmentLayoutStyles()} gap={2}>
              {carePlanState !== 'idle' &&
                !carePlanCalloutDismissed &&
                tab !== CARE_PLAN_TAB && (
                  <CarePlanCallout
                    state={carePlanState}
                    carePlan={carePlan}
                    onReview={() => setTab(CARE_PLAN_TAB)}
                    onDismiss={() => setCarePlanCalloutDismissed(true)}
                  />
                )}
              {!isDocumentTab(tab) && (
                <>
                  <EluveAdminControlsProvider>
                    <Stack gap={2}>
                      <Stack
                        justify="between"
                        align="center"
                        gap={2}
                        direction="horizontal"
                        wrap
                      >
                        <AppointmentName readonly={isReadonly} />
                        <Stack
                          direction="horizontal"
                          align="center"
                          gap={2}
                          fullWidth={false}
                        >
                          {appointmentStatus === 'COMPLETED' && (
                            <FeatureFlaggedComponent flag="LLM_OUTPUT_DETAILS">
                              <Link
                                to={`details/${DbEnums.LlmOutputType.BILLING_CODE_RECOMMENDATION}`}
                              >
                                <Button variant="outline" size="sm">
                                  <Icon
                                    name="Sparkles"
                                    size="xl"
                                    className="mr-1"
                                  />
                                  Experiments
                                </Button>
                              </Link>
                            </FeatureFlaggedComponent>
                          )}
                          <EluveAdminControlsToggle />
                        </Stack>
                      </Stack>
                      <EluveAdminControlsPanel />
                    </Stack>
                  </EluveAdminControlsProvider>
                  {appointmentStatus === 'CANCELLED' && (
                    <Stack className="pt-5" gap={2} direction="horizontal">
                      <Icon
                        name="CalendarX2Icon"
                        size="2xl"
                        className="text-text-negative"
                      />

                      <AlertDescription className="font-semibold text-text-negative">
                        This appointment was canceled in your EHR.
                      </AlertDescription>
                    </Stack>
                  )}
                </>
              )}
              <AppointmentTabContent tab={TRANSCRIPT_TAB}>
                <MicrophoneRequestAlert />
                <FeatureFlaggedComponent flag="INSIGHTS">
                  <AppointmentInsights />
                </FeatureFlaggedComponent>
                {skipNotStartedAppointmentLayout &&
                  appointmentStatus === 'NOT_STARTED' && (
                    <NewAppointmentForm
                      appointmentId={appointmentId}
                      tenantId={tenantId}
                      isCompactMicrophone
                      isSettingsAvailable
                    />
                  )}
                {appointmentStatus !== 'NOT_STARTED' && (
                  <AppointmentTranscription compact={isInsightsEnabled} />
                )}
                <HiddenForManualCharting>
                  <AdditionalNotes />
                </HiddenForManualCharting>
              </AppointmentTabContent>
              <AppointmentTabContent tab={SUMMARY_TAB}>
                <AppointmentSummaryResult />
              </AppointmentTabContent>
              {(carePlanState === 'generating' ||
                carePlanState === 'ready') && (
                <AppointmentTabContent tab={CARE_PLAN_TAB}>
                  {carePlan ? (
                    // Phased drafts (and pre-stamp drafts) get the chat/tailor tab;
                    // verbatim continuous/episodic drafts get the read-only view
                    // (ENG-4773). The shape is stamped on the draft so this branch
                    // needs no extra fetch.
                    (carePlanShapeOf(carePlan) ?? 'phased') === 'phased' ? (
                      <CarePlanTab
                        baseUrl={appConfig.VITE_API_DOMAIN}
                        appointmentId={appointmentId}
                        carePlan={carePlan}
                        onBackToNote={() => setTab(SUMMARY_TAB)}
                        onDraftChanged={refetchCarePlan}
                      />
                    ) : (
                      <CarePlanReadOnlyView
                        carePlan={carePlan}
                        onBackToNote={() => setTab(SUMMARY_TAB)}
                        onAccepted={refetchCarePlan}
                      />
                    )
                  ) : (
                    <Stack className="py-10" align="center" gap={2}>
                      <LoadingIcon />
                      <Text size="sm" color="secondary">
                        Preparing your care plan…
                      </Text>
                    </Stack>
                  )}
                </AppointmentTabContent>
              )}
              {isDocGenEnabled &&
                documents.map((doc) => (
                  <AppointmentTabContent
                    key={doc.id}
                    tab={toDocumentTabId(doc.id)}
                  >
                    {doc.contentType === 'application/pdf' ? (
                      <PdfDocumentTabContent
                        documentId={doc.id}
                        title={doc.title}
                        meta={doc.meta}
                      />
                    ) : (
                      <DocumentTabContent
                        documentId={doc.id}
                        tenantId={tenantId}
                        title={doc.title}
                        content={doc.content}
                      />
                    )}
                  </AppointmentTabContent>
                ))}
              {pdfFill && (
                <AppointmentTabContent tab={toDocumentTabId(PDF_FILL_DOC_ID)}>
                  <PdfFillTabContent
                    fill={pdfFill}
                    onStop={handlePdfFillStop}
                    onRetry={() =>
                      handleFillPdfForm(pdfFill.templateId, pdfFill.title)
                    }
                  />
                </AppointmentTabContent>
              )}
              {docGen && (
                <AppointmentTabContent tab={toDocumentTabId(DOC_GEN_ID)}>
                  <DocumentGeneratingTabContent
                    state={docGen}
                    onStop={handleDocGenStop}
                    onRetry={() => {
                      if (lastGenParamsRef.current) {
                        handleGenerate(lastGenParamsRef.current);
                      }
                    }}
                  />
                </AppointmentTabContent>
              )}
            </Stack>
          </div>

          <div className="col-start-1 row-start-3">
            {tab === TRANSCRIPT_TAB && appointmentStatus !== 'NOT_STARTED' && (
              <HiddenForManualCharting>
                <AppointmentTranscriptionFooter />
              </HiddenForManualCharting>
            )}
            {tab === SUMMARY_TAB && <AppointmentSummaryFooter />}
          </div>

          {/* DetailsSidebar-based rail: places itself into col 2, rows 2-3 of
              the host grid (fixed variant) or becomes a Sheet below 64rem. */}
          {tab === SUMMARY_TAB && (
            <FeatureFlaggedComponent flag="MEDPLUM">
              <MiniAppSummarySideRail
                apps={ALL_MINI_APPS}
                context={{
                  tenantId,
                  appointmentId,
                  patientId: patientId ?? undefined,
                  hasGeneratedNote: isSummaryAvailable,
                }}
              />
            </FeatureFlaggedComponent>
          )}
        </div>
      </Content>
    </>
  );
};
